//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { PojoMap } from 'pojo-maps';

import Cuid from '@models/Cuid';
import FileId from '@models/FileId';
import { DeetsItemId } from '@models/IDeetsModel';
import { IRightsPermissions } from '@models/IItemMetadata';
import { IMediaOverlayMetadata } from '@models/IMediaOverlayMetadata';
import {
  IMediaMetadata,
  INetworkItem,
  WithVideo,
  isGif,
  isVideo,
} from '@models/INetworkItem';
import INetworkUser from '@models/INetworkUser';
import { IProductSpatialTag } from '@models/IProduct';

export interface IMlsMediaItem {
  adMetadata?: IMlsMediaAdMetadata;
  beautifiedImageMetadata?: IMlsBeautifiedImageMetadata;
  cuid: Cuid;
  deetsItemId?: DeetsItemId;
  googleAssetFeedMetadata?: IMlsGoogleAssetFeedMetadata;
  instagramShopsMetadata?: IMlsInstagramShopsMetadata;
  labels?: string[];
  libraryMetadata?: IMlsLibraryMetadata;
  like2buyMetadata?: IMlsLike2BuyMetadata;
  like2BuyV2Metadata?: IMlsLike2BuyV2Metadata;
  rightsPermissions?: IRightsPermissions;
  mediaOverlayMetadata?: IMediaOverlayMetadata;
  networkItem?: INetworkItem;
  networkUser?: INetworkUser;
  productTags?: IMlsProductTag[];
  searchMetadata?: IMlsSearchMetadata;
  semanticTags?: IMlsSemanticTag[];
  sentimentMetadata?: IMlsSentimentMetadata;
  socialDetails?: IMlsSocialDetails;
  spamData?: ISpamData;
}

export interface IMlsBeautifiedImageMetadata {
  altFileId?: FileId;
  altFileIdMediaMetadata?: IMediaMetadata;
}

interface IMlsLike2BuyMetadata {
  isPublished: boolean;
  isHidden: boolean;
  pinnedPosition?: number;
}

interface IMlsLike2BuyV2Metadata {
  isPublished: boolean;
  isArchived: boolean;
  pinnedPosition?: number;
}

interface IMlsInstagramShopsMetadata {
  isFeatured: boolean;
  featuredProducts: string[];
}

export interface IMlsProductTag {
  id: string;
  position?: number;
  spatialTag?: IProductSpatialTag;
  metadata?: IMlsProductMetadata;
}

interface IMlsProductMetadata {
  name?: string;
  price?: string;
  url?: string;
  fileId?: FileId;
  isAvailable?: boolean;
  isCrawled?: boolean;
  isExpired?: boolean;
}

export interface IMlsSearchMetadata {
  similarityToActedOnScore?: number;
}

export interface ISpamData {
  isSpam?: boolean;
  score?: number;
  imageScore?: number;
  captionScore?: number;
}

interface IMlsAdCodeDetails {
  adCode: string;
  createdAt: Date;
  expirationTime?: Date;
  id: number;
}

interface IMlsMediaAdMetadata {
  adCode?: IMlsAdCodeDetails;
}

interface IMlsGoogleAssetFeedMetadata {
  acId?: string;
  headline?: string;
  label?: string;
  landingPageType: 'Static' | 'Dynamic';
  landingPageUrl: string;
  publishedTimestamp?: string;
}

interface IMlsLibraryMetadata {
  addedToLibraryTimestamp?: Date;
}

interface IMlsSemanticTag {
  confidence: number;
  tag: string;
}

interface IMlsSentimentMetadata {
  sentiment: 'Positive' | 'Neutral' | 'Negative';
}

interface IMlsSocialDetails {
  destinationUrl?: string;
  filter?: string;
  hashtags: string[];
  instagramPostType?: string;
  link: string;
  stats: IMlsSocialStats;
  taggedUsers: IMlsTaggedUser[];
  title?: string;
  user?: IMlsSocialUserDetails;
}

interface IMlsSocialStats {
  completionRate?: number;
  computedImpressions?: number;
  computedReach?: number;
  dislikes?: number;
  earnedMediaValue?: number;
  engagementRates?: IMlsSocialStatsEngagementRates;
  estimatedImpressions?: number;
  estimatedReach?: number;
  exits?: number;
  impressions?: number;
  interactions?: number;
  notes?: number;
  reach?: number;
  repins?: number;
  replies?: number;
  retweets?: number;
  saves?: number;
  shares?: number;
  tapsForward?: number;
  tapsBack?: number;
  videoViews?: number;
  views?: number;
}

interface IMlsSocialStatsEngagementRates {
  followerEngagementRate?: number;
  impressionsEngagementRate?: number;
  reachEngagementRate?: number;
}

interface IMlsTaggedUser {
  id?: string;
  username: string;
}

interface IMlsSocialUserDetails {
  biography?: string;
  fullName?: string;
  isBusiness?: boolean;
  stats?: IMlsSocialUserStats;
  website?: string;
}

interface IMlsSocialUserStats {
  followers?: number;
  following?: number;
  posts?: number;
}

export type WithFullMediaAndNetworkItem<
  T extends { mediaItems?: PojoMap<Cuid, IMlsMediaItem> },
> = T & {
  mediaItems: PojoMap<Cuid, IMlsMediaItem & { networkItem: INetworkItem }>;
};

/**
 * A helper to check that a given entity with a media item (i.e., an IG post)
 * contains at least one defined media item within its mediaItems map.
 */
export function hasFullMediaAndNetworkItem<
  T extends { mediaItems?: PojoMap<Cuid, IMlsMediaItem> },
>(post: T): post is WithFullMediaAndNetworkItem<T> {
  return !!(
    post.mediaItems &&
    Object.values(post.mediaItems).some((item) => item?.networkItem)
  );
}

type WithVideoNetworkItem<T extends IMlsMediaItem> = T & {
  networkItem: WithVideo<INetworkItem>;
};

/**
 * A helper to check whether a given media item contains a network item that is a video.
 */
export function hasVideo<T extends IMlsMediaItem>(
  mediaItem: T,
): mediaItem is WithVideoNetworkItem<T> {
  return !!(mediaItem.networkItem && isVideo(mediaItem.networkItem));
}

/**
 * A helper to check whether a given media item contains a network item that is a gif.
 */
export function hasGif<T extends IMlsMediaItem>(mediaItem: T): boolean {
  return !!(mediaItem.networkItem && isGif(mediaItem.networkItem));
}

/**
 * A helper to get the fileId for the media item.
 * Returns the beautified image fileId if it exists and falls back to the network item fileId otherwise.
 */
export function getFileId(mediaItem: IMlsMediaItem): FileId | undefined {
  const networkItemFileId = mediaItem.networkItem?.fileId;
  const beautifiedFileId = mediaItem.beautifiedImageMetadata?.altFileId;

  return beautifiedFileId ?? networkItemFileId;
}

/**
 * A helper to get the content type for the media item. (ex: 'image/jpeg', '/image/gif')
 */
export function getMediaContentType(
  mediaItem: IMlsMediaItem,
): string | undefined {
  return mediaItem.networkItem?.mediaMetadata?.contentType;
}

/**
 * A helper to get the media metadata for the media item.
 * Returns the beautified image metadata if it exists and falls back to the network item metadata otherwise.
 */
export function getMediaMetadata(
  mediaItem: IMlsMediaItem,
): IMediaMetadata | undefined {
  const networkItemMediaMetadata = mediaItem.networkItem?.mediaMetadata;
  const beautifiedMediaMetadata =
    mediaItem.beautifiedImageMetadata?.altFileIdMediaMetadata;

  return beautifiedMediaMetadata ?? networkItemMediaMetadata;
}

/**
 *  Helper to get the filesize for a piece of media
 *  if the media has beautified image data use that filesize,
 *  otherwise use its default filesize, otherwise set filesize to 0
 * */
export const getMediaFileSize = (mediaItem: IMlsMediaItem): number => {
  return getMediaMetadata(mediaItem)?.fileSize ?? 0;
};
